import { useMemo, useState } from "react";
import type { Grade, Station } from "../lib/types";
import { GRADES, GRADE_ORDER } from "../lib/grades";
import { formatValue } from "@core/lib/format";

interface Props {
  stations: Station[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  scopeName: string | null;
}

export default function StationList({ stations, selectedId, onSelect, scopeName }: Props) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Grade | "all">("all");

  const counts = useMemo(() => {
    const c = { all: stations.length } as Record<string, number>;
    for (const g of GRADE_ORDER) c[g] = stations.filter((s) => s.grade === g).length;
    return c;
  }, [stations]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return stations
      .filter((s) => filter === "all" || s.grade === filter)
      .filter((s) =>
        !needle ||
        s.siteId.toLowerCase().includes(needle) ||
        s.name.toLowerCase().includes(needle) ||
        s.stream.toLowerCase().includes(needle) ||
        s.watershed.toLowerCase().includes(needle) ||
        (s.location ?? "").toLowerCase().includes(needle))
      .sort((a, b) => a.stream.localeCompare(b.stream) || a.name.localeCompare(b.name));
  }, [stations, q, filter]);

  const chips = GRADE_ORDER.filter((g) => counts[g] > 0);

  return (
    <div className="list">
      <div className="list-controls">
        <input
          className="search"
          type="search"
          placeholder="Search site, stream or watershed"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search monitoring sites"
        />
        {chips.length > 1 && (
          <div className="chips" role="group" aria-label="Filter by grade">
            <button
              className={"chip" + (filter === "all" ? " on" : "")}
              onClick={() => setFilter("all")}
              aria-pressed={filter === "all"}
            >
              All <b>{counts.all}</b>
            </button>
            {chips.map((g) => (
              <button
                key={g}
                className={"chip" + (filter === g ? " on" : "")}
                onClick={() => setFilter(filter === g ? "all" : g)}
                aria-pressed={filter === g}
                style={{ ["--chip" as string]: GRADES[g].color }}
              >
                <i className="dot" /> {GRADES[g].label} <b>{counts[g]}</b>
              </button>
            ))}
          </div>
        )}
      </div>

      <ul className="rows">
        {shown.map((s) => {
          const on = s.key === selectedId;
          const g = GRADES[s.grade];
          return (
            <li key={s.key}>
              <button
                className={"row" + (on ? " on" : "")}
                onClick={() => onSelect(on ? null : s.key)}
                aria-current={on ? "true" : undefined}
              >
                <span className="row-bar" style={{ background: g.color }} />
                <span className="row-main">
                  <span className="row-id">{s.siteId}</span>
                  <span className="row-name">{s.name}</span>
                  <span className="row-meta">
                    {s.watershed}
                    {s.latest.year ? ` · ${s.latest.year}` : ""}
                    {s.latest.score !== null ? ` · score ${formatValue(s.latest.score, 0)}` : ""}
                  </span>
                </span>
                <span
                  className="row-grade"
                  style={{ background: g.color }}
                  title={`Grade ${g.label}`}
                >
                  {g.label}
                </span>
              </button>
            </li>
          );
        })}
        {shown.length === 0 && (
          <li className="empty-row">
            No sites match that{scopeName ? ` on ${scopeName}` : ""}.
          </li>
        )}
      </ul>
    </div>
  );
}
